import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const LOCATION_STORAGE_KEY = 'app:selected_location';

function loadPersistedLocation(): string {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCATION_STORAGE_KEY) : null;
    return typeof raw === 'string' ? raw : '';
  } catch {
    return '';
  }
}

interface LocationCodeContextType {
  selectedLocationCode: string;
  setSelectedLocationCode: (code: string) => void;
}

const LocationCodeContext = createContext<LocationCodeContextType | undefined>(undefined);

interface LocationCodeProviderProps {
  readonly children: ReactNode;
}

export function LocationCodeProvider({ children }: LocationCodeProviderProps) {
  const [selectedLocationCode, setSelectedLocationCode] = useState<string>(loadPersistedLocation);

  const handleSetSelectedLocationCode = useCallback((code: string) => {
    setSelectedLocationCode(code);
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, code);
    } catch {
      // ignore
    }
  }, [setSelectedLocationCode]);

  const contextValue = useMemo(
    () => ({ selectedLocationCode, setSelectedLocationCode: handleSetSelectedLocationCode }),
    [selectedLocationCode, handleSetSelectedLocationCode]
  );

  return (
    <LocationCodeContext.Provider value={contextValue}>
      {children}
    </LocationCodeContext.Provider>
  );
}

export function useLocationCode() {
  const context = useContext(LocationCodeContext);
  if (context === undefined) {
    return {
      selectedLocationCode: '',
      setSelectedLocationCode: () => { },
    };
  }
  return context;
}
